import { useField } from 'formik'
import React, { useState } from 'react'
import Select from 'react-select'
import Button from 'muicss/lib/react/button'

import './index.scss'

/**
 * Clear all the styles for the select component
 *
 * @returns {Proxy}
 */
const styleProxy = new Proxy(
  {},
  {
    get: () => () => { },
  },
)

/** CUSTOM COMPONENTS */

/**
 * Component for the single option component
 *
 * @param {Object} props the props
 * @param {Boolean} props.isSelected is the current option selected
 * @param {Object} props.innerProps props for the inner option component
 * @param {String} props.label label for the current optiong
 * @param {Object} props.data current option data
 * @returns {JSX.Element}
 */
const Option = (props) => {
  const {
    isSelected,
    innerProps,
    label,
    data,
  } = props

  return (
    <div
      className={`multi-select__option-wrapper${isSelected ? ' multi-select__option-wrapper--selected' : ''}`}
      onClick={() => innerProps.onClick(data)}
    >
      <div className="multi-select__option">{label}</div>
      <input type="checkbox" className={`multi-select__option-checkbox${isSelected ? ' multi-select__option-checkbox--checked' : ''}`} />
    </div>
  )
}

/**
 * Placeholder for the select component
 *
 * @param {Object} props the props
 * @param {Object} props.selectProps props for the select component
 * @returns {JSX.Element}
 */
const Placeholder = (props) => {
  const { selectProps } = props

  if (selectProps.menuIsOpen) {
    return <></>
  }

  return (
    <div className="multi-select__placeholder">
      {selectProps.placeholder}
    </div>
  )
}

/**
 * Test showed after selecting at least 1 option, indicates what is selected
 *
 * @param {Object} props the props
 * @param {Object} props.selectProps props for the select component
 * @returns {JSX.Element}
 */
const DropdownIndicator = (props) => {
  const { selectProps } = props
  const values = selectProps.value
  const selectedCount = values.length

  // return an empty object
  if (selectedCount === 0 || selectProps.menuIsOpen) {
    return <></>
  }

  const firstSelected = values[0].label
  return (
    <div className="multi-select__dropdown-indicator">
      <div className="multi-select__dropdown-indicator-name">{firstSelected}</div>
      {selectedCount > 1 && (
        <span>{` +${selectedCount - 1}${selectedCount > 2 ? ' others...' : '...'}`}</span>
      )}
    </div>
  )
}

/**
 * Wrapper for clear all and the multi value label
 *
 * @returns {JSX.Element}
 */
const MultiValueContainer = () => (
  <></>
)

const SelectAllButton = ({ options = [], setValue }) => {
  const [isAllSelected, setIsAllSelected] = useState(false)

  const handleAll = () => {
    if (isAllSelected) {
      setValue([])
      setIsAllSelected(false)
    } else {
      setValue(options)
      setIsAllSelected(true)
    }
  }

  return (
    <Button
      className="multi-select__all-button"
      type="button"
      variant="raised"
      color="accent"
      onClick={handleAll}
    >
      {isAllSelected ? 'Deselect All' : 'Select All'}
    </Button>
  )
}

/**
 * Multi select component
 *
 * @param {Object} props the props
 * @returns {JSX.Element}
 */
export default function SelectField (props) {
  // eslint-disable-next-line no-unused-vars
  const [_, state, { setValue, setTouched }] = useField(props.field.name)

  const onChange = (value) => {
    setValue(value)
  }

  return (
    <div className="multi-select">
      <Select
        {...props}
        value={state?.value}
        isMulti
        cacheOptions
        unstyled
        classNamePrefix="multi-select"
        styles={styleProxy}
        isSearchable
        onChange={onChange}
        onBlur={setTouched}
        components={{
          DropdownIndicator,
          MultiValueContainer,
          Option,
          Placeholder,
        }}
        hideSelectedOptions={false}
        isClearable={false}
        closeMenuOnSelect={false}
      />
      <SelectAllButton
        options={props.options}
        setValue={setValue}
      />
    </div>
  )
}
